/* eslint-disable react/jsx-handler-names */
import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import queryString from 'query-string';

import { moment } from '@eb/date';

import FilterButton from './FilterButton';
import { getRadioValues } from './FilterSection';

import { START_DATE, CUSTOM_DATE } from '../../constants';

import './FilterSection.scss';

const DATEPICKER_DEFAULT_DISPLAY_FORMAT = 'll';

export const SelectedDate = ({ urlParams }) => {
    let selectedDate = null;

    if (urlParams.start_date && urlParams.end_date) {
        const formattedStartDate = moment(urlParams.start_date).format(
            DATEPICKER_DEFAULT_DISPLAY_FORMAT,
        );
        const formattedEndDate = moment(urlParams.end_date).format(
            DATEPICKER_DEFAULT_DISPLAY_FORMAT,
        );

        selectedDate = (
            <div
                className="filter-section__button-date-subtitle"
                data-integration="filter-section__button-date-subtitle"
            >
                {formattedStartDate}
                <span className="eds-l-pad-left-2 eds-l-pad-right-1">-</span>
                {formattedEndDate}
            </div>
        );
    }

    return selectedDate;
};

// eslint-disable-next-line react/no-multi-comp
class DateFilterSection extends React.Component {
    static propTypes = {
        /**
         * The filter options to be presented to a user
         */
        filters: PropTypes.object.isRequired,
        /**
         * Callback to trigger when users pick a different filter
         */
        onOptionChange: PropTypes.func.isRequired,
        /**
         * The window location provided by withRouter
         */
        location: PropTypes.object.isRequired,
        /**
         * Callback to trigger when users pick a custom date
         */
        onDatePickerSelected: PropTypes.func.isRequired,
        /**
         * Component to render the Inline datepicker
         */
        DatePickerComponent: PropTypes.object.isRequired,

        /**
         * Relation between url parameters and the section's options
         */
        urlParameterMapping: PropTypes.object,
        /**
         * Whether the date picker drawer is active at the moment
         */
        isDrawerVisible: PropTypes.bool,
    };

    state = {
        // the currently selected filter in this section
        selectedFilter: Object.keys(this.props.filters)[0],
    };

    componentDidMount() {
        // when the page is loaded for the first time, we check the url to see whether
        // a filter should be set in the radio buttons
        this._parseUrl(this.props.location, false);
    }

    componentDidUpdate(prevProps) {
        // if the user clicks the back or forward button, we check the new url to see
        // if a filter needs to be applied
        if (this.props.location !== prevProps.location) {
            this._parseUrl(this.props.location, true);
        }
    }

    /**
     * Examine the url and apply any filters that are present in the url
     *
     * @param {string} url - the url of this page
     * @param {bool} shouldSearch - should we make an ajax search request to the server
     *
     */
    _parseUrl = (location, shouldSearch) => {
        // we special-case the custom date search filter by looking for the start_date param
        if (location.search.includes(START_DATE)) {
            this._handleOptionChange(CUSTOM_DATE, shouldSearch, false);

            return;
        }

        const { urlParameterMapping } = this.props;
        const url = location.pathname;

        if (urlParameterMapping) {
            const urlSplit = url.split('/');

            if (urlSplit.length > 3) {
                // individual filters are separated by double dashes
                const urlParams = urlSplit[3].split('--');

                // check every possible filter to see whether it exists in this url
                for (const key in urlParameterMapping) {
                    if (urlParams.includes(key)) {
                        this._handleOptionChange(
                            urlParameterMapping[key],
                            shouldSearch,
                            false,
                        );

                        // only one filter at a time so return when we find one
                        return;
                    }
                }

                // if we can't find any filters in the url, we apply the 'all' filter
                this._handleOptionChange('all', shouldSearch, false);
            }
        }
    };

    /**
     * Handle a change of the filter setting. This is triggered in three cases.
     *
     * 1. The initial page load. We want to set the radio value but not trigger a new search
     * because the server has already included the search results in the html.
     *
     * 2. The back or forward button is pressed. We want to set the radio button and trigger
     * a new search but we don't want to update the url path location.
     *
     * 3. The user has selected a new radio option.  We want to set the radio button, trigger
     * a new search, and update the url path to represent the new option state.
     *
     * @param {string} value - the new selected value to be set for the radio buttons
     * @param {bool} shouldSearch - should we make an ajax search request to the server
     * @param {bool} shouldUpdateRoute - should we set a new location in the url
     *
     */
    _handleOptionChange = (value, shouldSearch, shouldUpdateRoute) => {
        const { onOptionChange, onDatePickerSelected } = this.props;

        if (value === CUSTOM_DATE && shouldUpdateRoute) {
            onDatePickerSelected(true);
        }

        if (value !== this.state.selectedFilter) {
            this.setState({ selectedFilter: value });

            if (shouldSearch && value !== CUSTOM_DATE) {
                onOptionChange(value, shouldUpdateRoute);
            }
        }
    };

    render() {
        const {
            filters,
            location,
            DatePickerComponent,
            isMobile,
            isDrawerVisible,
        } = this.props;

        // Only render the date picker component when mobile
        if (isMobile && isDrawerVisible) {
            return (
                <div data-integration="filter-panel__date-picker-drawer">
                    {DatePickerComponent}
                </div>
            );
        }

        const { selectedFilter } = this.state;
        const urlParams = queryString.parse(location.search.replace('?', ''));
        const { radioValues } = getRadioValues(filters);
        const hasCustomDate = (value) =>
            value === CUSTOM_DATE && selectedFilter === CUSTOM_DATE;
        const shouldShowInlineDatePicker = (value) =>
            (!isMobile && hasCustomDate(value)) ||
            (isMobile && isDrawerVisible);

        return (
            <div className="filter-section" data-integration="filter-section">
                {radioValues.map(({ display, value }) => (
                    <div key={value}>
                        <FilterButton
                            isMobile={isMobile}
                            onClick={() =>
                                this._handleOptionChange(value, true, true)
                            }
                            value={value}
                            isActive={this.state.selectedFilter === value}
                        >
                            <span>
                                {display}
                                {hasCustomDate(value) && (
                                    <SelectedDate urlParams={urlParams} />
                                )}
                            </span>
                        </FilterButton>
                        {shouldShowInlineDatePicker(value) && (
                            <>{DatePickerComponent}</>
                        )}
                    </div>
                ))}
            </div>
        );
    }
}

// withRouter adds a location field to props which allows us to watch for url changes
export default withRouter(DateFilterSection);
